import { Place } from '@mui/icons-material';
import AutocompleteComponent from 'components/design-system/Autocomplete/Autocomplete';
import InputComponent from 'components/design-system/input/Input';
import LabelComponent from 'components/design-system/Label/Label';
import Text from 'components/design-system/Text/Text';
import { PERMISOS } from 'const/Permisos';

/**
 *
 * @param {*} values
 * @param {*} touched
 * @param {*} errors
 * @param {*} isLoadingEmpresas
 * @param {*} empresas
 * @param {*} setFieldValue
 * @param {*} setFieldTouched
 * @param {*} setEmpresaSeleccionada
 * @param {*} titleLabelEmpresa : 'Nombre de la empresa en el label'
 * @param {*} classSelectNegocio : 'Clase para el select de negocio'
 * @returns
 */
// EmpresaSelector
const EmpresaSelector = ({
  values,
  touched,
  errors,
  isLoadingEmpresas,
  empresas,
  setFieldValue,
  setFieldTouched,
  setEmpresaSeleccionada,
  titleLabelEmpresa = 'Negocio',
  classSelectNegocio = 'col-span-12 xs:col-span-4'
}) => {
  const handleChange = (ev, object, reason) => {
    if (reason === 'clear') {
      setFieldValue('empresa', null);
      setEmpresaSeleccionada(null);
      setFieldValue('sucursal', null);
    } else {
      setEmpresaSeleccionada(object);
      setFieldValue('empresa', object);
      setFieldValue('sucursal', null);
    }
  };
  return (
    <div className={classSelectNegocio}>
      <AutocompleteComponent
        id="empresa"
        name="empresa"
        accesor="nombreEmpresa"
        options={empresas}
        disabled={isLoadingEmpresas}
        onChange={handleChange}
        getOptionLabel={option => option.nombreEmpresa || ''}
        value={values.empresa}
        onBlur={() => setFieldTouched('empresa')}
      >
        <InputComponent
          fullWidth
          className="h-9"
          touched={touched.empresa}
          estado={
            errors.empresa ? { tipo: 'error', mensaje: errors.empresa } : null
          }
          label={<LabelComponent>{titleLabelEmpresa}</LabelComponent>}
        />
      </AutocompleteComponent>
    </div>
  );
};

/**
 *
 * @param {*} values
 * @param {*} touched
 * @param {*} errors
 * @param {*} isSubmitting
 * @param {*} isLoadingSucursales
 * @param {*} sucursales
 * @param {*} setFieldValue
 * @param {*} setFieldTouched
 * @param {*} titleLabelSucursal : 'Nombre de la sucursal en el label'
 * @param {*} classSelectSucursal : 'Clase para el select de sucursal'
 * @returns
 */

// SucursalSelector
const SucursalSelector = ({
  values,
  touched,
  errors,
  isSubmitting,
  isLoadingSucursales,
  sucursales,
  setFieldValue,
  setFieldTouched,
  titleLabelSucursal = 'Sucursal de origen',
  classSelectSucursal = 'col-span-12 xs:col-span-4',
  isAdmin = false
}) => {
  const handleSucursalChange = (event, newValue, reason) => {
    if (reason === 'clear') {
      setFieldValue('nombreDireccionSucursal', undefined);
      setFieldValue('sucursal', '');
      return;
    }

    const direccion = newValue?.direccion || {};
    const nombreDireccion = `${direccion.nombreVia}, ${direccion.comuna?.nombreComuna}, Región ${direccion.comuna?.region?.nombreRegion}`;

    setFieldValue('nombreDireccionSucursal', nombreDireccion);
    setFieldValue('sucursal', newValue);
    setFieldTouched('fechaTransaccion');
  };

  return (
    <div className={classSelectSucursal}>
      <AutocompleteComponent
        accesor="nombreSucursal"
        clearOnEscape
        disabled={
          isSubmitting || isLoadingSucursales || sucursales.length === 0
        }
        fullWidth
        name="sucursal"
        options={sucursales}
        value={values.sucursal || ''}
        onChange={handleSucursalChange}
        getOptionLabel={option => option.nombreSucursal || ''}
        noOptionsText={isLoadingSucursales ? 'Cargando...' : 'Sin opciones'}
      >
        <InputComponent
          autoComplete="off"
          disabled={isSubmitting || isLoadingSucursales}
          fullWidth
          onBlur={() => setFieldTouched('sucursal')}
          touched={touched.sucursal}
          placeholder={
            isLoadingSucursales ? 'Cargando...' : 'Selecciona una opción'
          }
          estado={
            errors.sucursal ? { tipo: 'error', mensaje: errors.sucursal } : null
          }
          label={<LabelComponent>{titleLabelSucursal}</LabelComponent>}
        />
      </AutocompleteComponent>
    </div>
  );
};

// DireccionDisplay
const DireccionDisplay = ({ direccion, spanSize = 4 }) => (
  <div className={`col-span-12 xs:col-span-${spanSize} pl-2 pt-1 mt-6`}>
    <div className="address-icon">
      <Place className="text-uv-secondary-0" />
      <Text>{direccion || 'Seleccionar sucursal'}</Text>
    </div>
  </div>
);

/**
 *
 * @param {*} isTienePermisos : funcion que retorna si el usuario tiene permisos
 * @param {*} isUseDireccion  : booleano para mostrar componente que visualiza la direccion
 * @param {*} className : 'Clase para el contenedor'
 * @param {*} isAdmin : booleano para saber si el usuario es admin
 * @param {*} props
 * @returns
 */

// SelectorContainer
const SelectorNegociosContainer = ({
  isTienePermisos,
  isAdmin,
  isUseDireccion = true,
  className = 'grid grid-cols-12 gap-4 align-middle mt-2',
  ...props
}) => {
  return (
    <div className={className}>
      {isTienePermisos(PERMISOS.administracionSucursales) ? (
        <>
          <EmpresaSelector {...props} />
          <SucursalSelector {...props} isAdmin={true} />
          {isUseDireccion && (
            <DireccionDisplay
              direccion={props.values.nombreDireccionSucursal}
            />
          )}
        </>
      ) : (
        <>
          <SucursalSelector {...props} />
          {isUseDireccion && (
            <DireccionDisplay
              direccion={props.values.nombreDireccionSucursal}
              spanSize={8}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SelectorNegociosContainer;
